// Spec §2.5 Tab 1 [1a] — consolidation preview + apply banner. Reads the business-wide
// plan from POST /ord/consolidation/preview (authorized orders not yet linked to a DN,
// grouped by bucket-key) and applies it via /ord/consolidation/bulk-create. Both routes
// are gated by ORD_DN_CREATE on the BE; without it there is no plan to show.

import { Box, Group, Text } from "@mantine/core"
import { InfoIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { ConsolidationApi } from "../../apis"
import { Dialog } from "../../utils/confirm-modal"
import { message } from "../../utils/message"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import classes from "./pending.module.css"

interface Props {
  /** Called after a successful bulk-create so the parent can refresh the list. */
  onConsolidated?: () => void
}

export function ConsolidationPreview({ onConsolidated }: Props) {
  const { t } = useTranslation("delivery")
  const { permissionState } = useDeliveryActions()
  const perm = permissionState("consolidate")
  const [submitting, setSubmitting] = useState(false)

  const { data, isLoading, mutate } = useSWR(perm.allowed ? ["consolidation-preview"] : null, () => ConsolidationApi.preview(), {
    revalidateOnFocus: false,
  })

  if (!perm.allowed || isLoading || !data || data.totalOrders === 0) return null

  const counts = { orders: data.totalOrders, dns: data.totalDnsToCreate }

  const apply = async () => {
    setSubmitting(true)
    try {
      const res = await ConsolidationApi.bulkCreate()
      message.success(t("manage.consolidate.success", { dns: res.dnsCreated, orders: res.ordersConsolidated }))
      await mutate()
      onConsolidated?.()
    } catch (err) {
      message.error(translateBackendError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleConsolidate = () =>
    Dialog.confirm(t("manage.consolidate.confirm", counts), (key) => {
      if (key === "confirm") apply()
    })

  return (
    <Group className={classes.banner} justify="space-between" wrap="nowrap" gap="md">
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <Box c="var(--nw-text-2)" mt={2}>
          <InfoIcon size={20} />
        </Box>
        <Box>
          <Text fw={600} fz="var(--nw-fz-lead)">
            {t("manage.consolidate.title", counts)}
          </Text>
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {t("manage.consolidate.hint", counts)}
          </Text>
        </Box>
      </Group>
      <DeliveryActionButton state={perm} color="dark" loading={submitting} onClick={handleConsolidate}>
        {t("manage.consolidate.action")}
      </DeliveryActionButton>
    </Group>
  )
}
